/* eslint-disable no-param-reassign */
/* eslint-disable camelcase */
import * as Yup from 'yup';
import _ from 'lodash';

import { CONSTANT } from '../utils/constant';
import { getLocalizedStrings } from '../misc';

const {
  KEYS: {
    CHANNELS: { DEALER, CSD, NBD, ADT, HEALTH, BCX_CARE, CORP_STORE },
    ROLES: {
      ADMIN,
      SALES_REP,
      ORDER_FULFILLMENT,
      PROPOSAL_HOUSE,
      SALES_MANAGER,
      SALES_SUPPORT
    }
  }
} = CONSTANT;

const {
  USERS: {
    FORM: {
      VALIDATION_MESSAGE: { REQUIRED, EMAIL, NUMERIC, ALPHANUMERIC, MIN, MAX }
    }
  }
} = getLocalizedStrings();

const isOutletRequired = (role, channel) =>
  ![ADMIN, PROPOSAL_HOUSE, ORDER_FULFILLMENT, SALES_SUPPORT].includes(role) &&
  ![ADT, BCX_CARE, CSD, NBD, HEALTH, CORP_STORE].includes(
    _.get(channel, '0.id', '')
  );

const isCpmsRequired = (role, channel) =>
  ![ADMIN, PROPOSAL_HOUSE, ORDER_FULFILLMENT, SALES_SUPPORT].includes(role) &&
  ![ADT, BCX_CARE, CSD, NBD, HEALTH, CORP_STORE].includes(
    _.get(channel, '0.id', '')
  );

const isXidRequired = (role, channel) =>
  [ADMIN, ORDER_FULFILLMENT, PROPOSAL_HOUSE, SALES_SUPPORT].includes(role) ||
  [ADT, BCX_CARE, CSD, NBD, HEALTH, CORP_STORE].includes(
    _.get(channel, '0.id', '')
  );

const isDealershipRequired = (role, channel, multiChannels) => {
  const valueChannel =
    role === SALES_SUPPORT ? multiChannels : [_.get(channel, '0.id', '')];
  return (
    [SALES_REP, SALES_MANAGER].includes(role) &&
    valueChannel &&
    valueChannel.includes(DEALER)
  );
};

const isChannelRequired = role => [SALES_MANAGER, SALES_REP].includes(role);

const isStoreCodeRequired = (role, channel, multiChannels) => {
  const valueChannel =
    role === SALES_SUPPORT ? multiChannels : [_.get(channel, '0.id', '')];
  return (
    [SALES_REP, SALES_MANAGER, SALES_SUPPORT].includes(role) &&
    valueChannel &&
    valueChannel.includes(CORP_STORE)
  );
};

const validationJSON = Yup.object().shape({
  roleId: Yup.mixed().required(() => {
    return REQUIRED;
  }),
  firstName: Yup.string().required(() => {
    return REQUIRED;
  }),
  lastName: Yup.string().required(() => {
    return REQUIRED;
  }),
  email: Yup.string()
    .email(() => {
      return EMAIL;
    })
    .required(() => {
      return REQUIRED;
    }),
  province: Yup.mixed().when('roleId', {
    is: val => val !== SALES_SUPPORT,
    then: Yup.string().required(REQUIRED),
    otherwise: Yup.string()
  }),
  provinceIds: Yup.mixed().when('roleId', {
    is: SALES_SUPPORT,
    then: Yup.array()
      .of(Yup.string())
      .required(REQUIRED)
      .min(1),
    otherwise: Yup.array()
  }),
  contactNumber: Yup.string()
    .required(() => {
      const {
        USERS: {
          USER_AUTHENTICATION: {
            REGISTRATION_SCHEMA_MESSAGES: { CONTACT_REQUIRED }
          }
        }
      } = getLocalizedStrings();
      return CONTACT_REQUIRED;
    })
    .min(10, 'Contact Number must be 10 digits')
    .max(10, 'Contact Number no more than 10 digits')
    .matches(/^[0-9]+$/, () => {
      const {
        USERS: {
          USER_AUTHENTICATION: {
            REGISTRATION_SCHEMA_MESSAGES: { MIN_BE_DIGIT_10 }
          }
        }
      } = getLocalizedStrings();
      return MIN_BE_DIGIT_10;
    }),
  channelId: Yup.mixed().when('roleId', {
    is: isChannelRequired,
    then: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.string(),
          label: Yup.string()
        })
      )
      .required(() => REQUIRED)
  }),
  channelIds: Yup.mixed().when('roleId', {
    is: val => val === SALES_SUPPORT,
    then: Yup.array()
      .of(Yup.string())
      .required(REQUIRED)
      .min(1),
    otherwise: Yup.array()
  }),
  outlet: Yup.string().when(['roleId', 'channelId'], {
    is: isOutletRequired,
    then: Yup.string()
      .required(() => {
        return REQUIRED;
      })
      .matches(/^[a-zA-Z0-9]+$/, () => {
        return ALPHANUMERIC;
      })
      .min(10, () => {
        return MIN['10'];
      })
      .max(10, () => {
        return MAX['10'];
      }),
    otherwise: Yup.string()
  }),
  cpmsId: Yup.string().when(['roleId', 'channelId'], {
    is: isCpmsRequired,
    then: Yup.string()
      .required(() => {
        return REQUIRED;
      })
      .min(4, () => {
        return MIN['4'];
      })
      .max(4, () => {
        return MAX['4'];
      }),
    otherwise: Yup.string()
  }),
  storeCode: Yup.string().when(['roleId', 'channelId', 'channelIds'], {
    is: isStoreCodeRequired,
    then: Yup.string()
      .required(() => {
        return REQUIRED;
      })
      .min(5, () => {
        return MIN['5'];
      })
      .max(5, () => {
        return MAX['5'];
      }),
    otherwise: Yup.string()
  }),
  xid: Yup.string().when(['roleId', 'channelId'], {
    is: isXidRequired,
    then: Yup.string()
      .required(() => {
        return REQUIRED;
      })
      .min(7, () => {
        return MIN['7'];
      })
      .max(7, () => {
        return MAX['7'];
      }),
    otherwise: Yup.string()
  }),
  dealership: Yup.string().when(['roleId', 'channelId', 'channelIds'], {
    is: isDealershipRequired,
    then: Yup.string().required(() => {
      return REQUIRED;
    }),
    otherwise: Yup.string()
  }),
  channelOrgCode: Yup.string().when(['roleId', 'channelId', 'channelIds'], {
    is: isDealershipRequired,
    then: Yup.string()
      .required(() => {
        return REQUIRED;
      })
      .min(5, () => {
        return MIN['5'];
      })
      .max(5, () => {
        return MAX['5'];
      }),
    otherwise: Yup.string()
  }),
  salesManager: Yup.string().when('roleId', {
    is: ADMIN,
    then: Yup.string(),
    otherwise: Yup.string().required(() => {
      return REQUIRED;
    })
  }),
  salesManagerEmail: Yup.string().when('roleId', {
    is: ADMIN,
    then: Yup.string(),
    otherwise: Yup.string()
      .required(() => {
        return REQUIRED;
      })
      .email(() => {
        return EMAIL;
      })
  })
});

export default validationJSON;
